const SELECTORS = {
  product: '[data-section-id][data-product]',
  toggle: '[data-find-in-store-toggle]',
  sizeInput: '[data-find-in-store-size]',
  stores: '[data-find-in-store-stores]',
  store: '[data-find-in-store-store]',
  badge: '[data-find-in-store-badge]',
  note: '[data-find-in-store-note]',
  variantId: '[data-product-select]',
};

const BADGE_STATES = ['in-stock', 'low-stock', 'out-of-stock', 'loading'];

const escapeSelectorValue = (value) => {
  if (window.CSS && typeof window.CSS.escape === 'function') {
    return window.CSS.escape(value);
  }

  return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
};

class FindInStore {
  constructor(modal) {
    this.modal = modal;
    this.toggle = Array.from(document.querySelectorAll(SELECTORS.toggle)).find(
      (button) => button.getAttribute('aria-controls') === modal.id
    );

    if (!this.toggle || modal.dataset.findInStoreReady) {
      return;
    }

    modal.dataset.findInStoreReady = 'true';

    this.strings = {
      'in-stock': modal.dataset.findInStoreInStock,
      'low-stock': modal.dataset.findInStoreLowStock,
      'out-of-stock': modal.dataset.findInStoreOutOfStock,
      loading: modal.dataset.findInStoreLoading,
    };
    this.product = this.findProduct();
    this.optionPosition = modal.dataset.findInStoreOptionPosition;
    this.fallbackVariantId = modal.dataset.findInStoreVariantId || null;
    this.sizeInputs = Array.from(modal.querySelectorAll(SELECTORS.sizeInput));
    this.storeList = modal.querySelector(SELECTORS.stores);
    this.storeRows = Array.from(modal.querySelectorAll(SELECTORS.store));
    this.note = modal.querySelector(SELECTORS.note);
    this.cache = new Map();
    this.requestToken = 0;
    this.inflightVariant = null;
    this.hasOpened = false;

    this.bindEvents();
    this.syncWhenProductIsReady();
  }

  findProduct() {
    const sectionId = this.modal.dataset.findInStoreProductSection;
    const product = Array.from(document.querySelectorAll(SELECTORS.product)).find(
      (element) => element.dataset.sectionId === sectionId
    );

    return product || document;
  }

  bindEvents() {
    this.sizeInputs.forEach((input) => {
      input.addEventListener('change', ({ currentTarget }) => {
        this.selectSizeOnProduct(currentTarget.value);
        this.update();
      });
    });

    this.product.addEventListener('theme:variant:change', () => {
      if (this.hasOpened) {
        this.update();
      } else {
        this.syncSizeStates();
      }
    });

    this.toggle.addEventListener('click', () => {
      this.hasOpened = true;
      this.syncSizeStates();
      this.ensureSizeSelected();
      this.update();
    });
  }

  syncWhenProductIsReady() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.syncSizeStates(), { once: true });
      return;
    }

    this.syncSizeStates();
  }

  productOptionWrapper() {
    if (!this.optionPosition) {
      return null;
    }

    return this.product.querySelector(
      `[data-option-position="${escapeSelectorValue(this.optionPosition)}"]`
    );
  }

  productSizeInput(value) {
    const wrapper = this.productOptionWrapper();

    if (!wrapper) {
      return null;
    }

    return wrapper.querySelector(
      `.radio__input[value="${escapeSelectorValue(value)}"]`
    );
  }

  selectSizeOnProduct(value) {
    const wrapper = this.productOptionWrapper();

    if (!wrapper) {
      return;
    }

    const radio = this.productSizeInput(value);

    if (radio) {
      if (radio.checked) {
        return;
      }

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const popoutOption = wrapper.querySelector(
      `[data-popout-option][data-value="${escapeSelectorValue(value)}"]`
    );

    if (popoutOption) {
      popoutOption.click();
    }
  }

  hasState(element, state) {
    return Boolean(
      element &&
        (element.classList.contains(state) ||
          (element.parentElement && element.parentElement.classList.contains(state)))
    );
  }

  setState(input, state, isActive) {
    input.classList.toggle(state, isActive);

    if (input.parentElement) {
      input.parentElement.classList.toggle(state, isActive);
    }
  }

  isUnavailable(input) {
    return this.hasState(input, 'sold-out') || this.hasState(input, 'unavailable');
  }

  syncSizeStates() {
    this.sizeInputs.forEach((input) => {
      const source = this.productSizeInput(input.value);
      const unavailable = this.hasState(source, 'unavailable');

      this.setState(input, 'sold-out', this.hasState(source, 'sold-out'));
      this.setState(input, 'unavailable', unavailable);
      input.disabled = unavailable;

      if (source && source.checked) {
        input.checked = true;
      }
    });
  }

  currentVariantId() {
    const input = this.product.querySelector(SELECTORS.variantId);
    const fromForm = input ? input.value : '';

    // The form input wins once the theme has populated it, but it can render
    // empty, so fall back to the id Liquid resolved for this render.
    return fromForm || this.fallbackVariantId || null;
  }

  selectedSize() {
    const selected = this.sizeInputs.find((input) => input.checked);
    return selected ? selected.value : null;
  }

  ensureSizeSelected() {
    if (!this.sizeInputs.length || this.selectedSize()) {
      return;
    }

    const availableInput = this.sizeInputs.find((input) => !this.isUnavailable(input));

    if (availableInput) {
      availableInput.checked = true;
      this.selectSizeOnProduct(availableInput.value);
    }
  }

  setBadge(row, state) {
    const badge = row.querySelector(SELECTORS.badge);

    if (!badge) {
      return;
    }

    BADGE_STATES.forEach((badgeState) => {
      badge.classList.remove(`find-in-store__badge--${badgeState}`);
    });

    if (!state || !this.strings[state]) {
      badge.hidden = true;
      badge.textContent = '';
      return;
    }

    badge.classList.add(`find-in-store__badge--${state}`);
    badge.textContent = this.strings[state];
    badge.hidden = false;
  }

  clearBadges() {
    this.storeRows.forEach((row) => this.setBadge(row, null));
  }

  setLoading(isLoading) {
    if (this.storeList) {
      this.storeList.setAttribute('aria-busy', String(isLoading));
    }

    if (isLoading) {
      this.storeRows.forEach((row) => this.setBadge(row, 'loading'));
    }
  }

  renderAvailability(data) {
    const stores = (data && data.stores) || [];
    const availabilityByLocation = new Map(
      stores.map(({ location_id: locationId, state }) => [String(locationId), state])
    );

    this.storeRows.forEach((row) => {
      this.setBadge(row, availabilityByLocation.get(row.dataset.locationId));
    });
  }

  async update() {
    this.syncSizeStates();

    if (this.sizeInputs.length && !this.selectedSize()) {
      if (this.note) {
        this.note.hidden = false;
      }

      this.setLoading(false);
      this.clearBadges();
      return;
    }

    if (this.note) {
      this.note.hidden = true;
    }

    const variantId = this.currentVariantId();

    if (!variantId) {
      this.setLoading(false);
      this.clearBadges();
      return;
    }

    if (this.cache.has(variantId)) {
      this.setLoading(false);
      this.renderAvailability(this.cache.get(variantId));
      return;
    }

    if (this.inflightVariant === variantId) {
      return;
    }

    const token = ++this.requestToken;
    this.inflightVariant = variantId;
    this.setLoading(true);

    try {
      const response = await fetch(
        `/variants/${variantId}/?section_id=find-in-store-availability`
      );

      if (!response.ok) {
        throw new Error('Unable to load store availability');
      }

      const holder = document.createElement('div');
      holder.innerHTML = await response.text();

      const payload = holder.querySelector('[data-find-in-store-availability]');

      if (!payload) {
        throw new Error('Missing availability payload');
      }

      const data = JSON.parse(payload.textContent);
      this.cache.set(variantId, data);

      if (token === this.requestToken) {
        this.renderAvailability(data);
      }
    } catch (error) {
      if (token === this.requestToken) {
        this.clearBadges();
      }
    } finally {
      if (this.inflightVariant === variantId) {
        this.inflightVariant = null;
      }

      if (token === this.requestToken) {
        this.setLoading(false);
      }
    }
  }
}

const findInStoreModals = (scope) => {
  const modals = Array.from(scope.querySelectorAll('[data-find-in-store]'));

  if (scope.matches && scope.matches('[data-find-in-store]')) {
    modals.unshift(scope);
  }

  return modals;
};

const initializeFindInStore = (scope = document) => {
  findInStoreModals(scope).forEach((modal) => new FindInStore(modal));
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeFindInStore(), { once: true });
} else {
  initializeFindInStore();
}

document.addEventListener('shopify:section:load', ({ target }) => initializeFindInStore(target));
